import axios, { AxiosRequestConfig } from 'axios';
import { refreshToken } from './api';

interface RetryRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

// リフレッシュ処理の状態管理
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
}> = [];

// 待機中のリクエストを処理する関数
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

export const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true, // Cookieを自動送信
});

// インターセプターを定義する関数
// これをAuthProviderで呼び出す
export const setupInterceptors = (
  getAccessToken: () => string | null,
  updateAccessToken: (token: string | null) => void,
  logout: () => void
) => {
  // 古いインターセプターを削除
  axiosInstance.interceptors.request.clear();
  axiosInstance.interceptors.response.clear();
  let hasShownForbiddenAlert = false;
  axiosInstance.interceptors.request.use((config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config as RetryRequestConfig;

      if (error.response?.status === 403 && !hasShownForbiddenAlert) {
        hasShownForbiddenAlert = true;
        alert('認証に失敗しました。再度ログインしてください。');
        logout();
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            const requestCopy = { ...originalRequest };
            failedQueue.push({
              resolve: (newToken: string) => {
                resolve(
                  axiosInstance({
                    ...requestCopy,
                    headers: {
                      ...requestCopy.headers,
                      Authorization: `Bearer ${newToken}`,
                    },
                  })
                );
              },
              reject,
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const response = await refreshToken();
          const newAccessToken = response.accessToken;

          // 即座にupdateAccessTokenを呼ぶ
          updateAccessToken(newAccessToken);

          // 待機中のリクエストにも新しいトークンを設定
          processQueue(null, newAccessToken);

          // 元のリクエストにも新しいトークンを設定して再実行
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          alert('ログイン状態が無効になりました。再度ログインしてください。');
          logout();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
      return Promise.reject(error);
    }
  );
};
